import React, { useMemo, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import * as Constants from "../../constants/constants"
import { Line } from 'react-chartjs-2';
import { AgGridReact } from 'ag-grid-react';
import { SideBarModule, ColumnsToolPanelModule } from 'ag-grid-enterprise';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const DEFAULT_COLS = new Set([
    "Trans. Date", "W.O. #", "Parts Cost", "Sublet Cost", "Labor Price",
    "Parts Price", "Sublet Price", "Tax Coll.", "Credit Amount", "Amount Recvd",
    "W.O. Total"
]);

const currencyFormatter = (params) =>
    params.value != null
        ? '$' + params.value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        : '--';

const CHART_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ReportsTable(props) {
    const { reports, splits = [], methodTotals, summary } = props;
    const totals = props.totals[0] ?? {};
    const gridRef = useRef(null);

    const chartOptions = {
        scales: { y: { type: 'linear', display: true } },
        plugins: {
            tooltip: {
                enabled: true,
                callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString()}`,
                    title: (ctx) => `Month: ${ctx[0].label}`,
                },
            },
        },
    };

    const rowData = useMemo(() => reports.map(item => {
        const isSplit = item.Totals.PaymentMethod === "Split";
        const splitMatch = splits.find(s => s.Workorder.WorkorderNumber === item.Workorder.WorkorderNumber);
        const amountRecvd = isSplit
            ? (item.Totals.AmountRecvd ?? 0)
            : splitMatch ? splitMatch.amount : (item.Totals.WorkorderTotal ?? 0);

        return {
            _raw: item,
            transDate: isSplit
                ? (item.Totals.TransDate ? Constants.dateFormatter(item.Totals.TransDate) : '--')
                : Constants.dateFormatter(item.Totals.DateClosed),
            woNumber: item.Workorder.WorkorderNumber,
            customer: `${item.Customer.Firstname ? item.Customer.Firstname + ' ' : ''}${item.Customer.Lastname}`,
            method: isSplit
                ? (item.Totals.TransType ? `${item.Totals.TransType}*` : '--')
                : (item.Payments[0]?.Type || ''),
            laborCost: null,
            wasteCost: isSplit ? null : (item.Totals.WasteCost ?? 0),
            partsCost: isSplit ? null : (item.Totals.PartsCost ?? 0),
            subletCost: isSplit ? null : (item.Totals.SubletCost ?? 0),
            laborPrice: isSplit ? null : (item.Totals.LaborSubtotal ?? 0),
            partsPrice: isSplit ? null : (item.Totals.PartsSubtotal ?? 0),
            subletPrice: isSplit ? null : (item.Totals.SubletSubtotal ?? 0),
            discAmount: null,
            taxColl: isSplit ? null : (item.Totals.WorkorderTax ?? 0),
            creditAmount: isSplit ? null : (item.Totals.CreditSubtotal ?? 0),
            amountRecvd,
            woTotal: isSplit ? null : (item.Totals.WorkorderTotal ?? 0),
            woProfit: null,
        };
    }), [reports, splits]);

    const pinnedBottomRowData = useMemo(() => {
        if (!reports.length) return [];
        return [{
            transDate: 'TOTALS',
            wasteCost: totals.WasteCost ?? 0,
            partsCost: totals.PartsCost ?? 0,
            subletCost: totals.SubletCost ?? 0,
            laborPrice: totals.LaborPrice ?? 0,
            partsPrice: totals.PartsPrice ?? 0,
            subletPrice: totals.SubletPrice ?? 0,
            taxColl: totals.WOtax ?? 0,
            creditAmount: totals.Credit ?? 0,
            amountRecvd: methodTotals.reduce((acc, item) => acc + item.total, 0),
            woTotal: totals.WOtotal ?? 0,
        }];
    }, [totals, methodTotals, reports.length]);

    const columnDefs = useMemo(() => [
        { flex:1, headerName: 'Trans. Date', field: 'transDate', filter: true, hide: !DEFAULT_COLS.has("Trans. Date"), cellStyle: { textAlign: 'left', paddingLeft: '4px', paddingRight: '4px' }, headerClass: '' },
        { flex:1, headerName: 'W.O. #', field: 'woNumber', filter: true, hide: !DEFAULT_COLS.has("W.O. #") },
        { flex:1, headerName: 'Customer', field: 'customer', filter: true, hide: !DEFAULT_COLS.has("Customer"), cellStyle: { textAlign: 'left', paddingLeft: '4px', paddingRight: '4px' }, headerClass: '' },
        { flex:1, headerName: 'Method', field: 'method', filter: true, hide: !DEFAULT_COLS.has("Method"), cellStyle: { textAlign: 'left', paddingLeft: '4px', paddingRight: '4px' }, headerClass: '' },
        { flex:1, headerName: 'Labor Cost', field: 'laborCost', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Labor Cost"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Waste Cost', field: 'wasteCost', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Waste Cost"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Parts Cost', field: 'partsCost', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Parts Cost"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Sublet Cost', field: 'subletCost', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Sublet Cost"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Labor Price', field: 'laborPrice', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Labor Price"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Parts Price', field: 'partsPrice', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Parts Price"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Sublet Price', field: 'subletPrice', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Sublet Price"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Disc. Amount', field: 'discAmount', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Disc. Amount"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Tax Coll.', field: 'taxColl', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Tax Coll."), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Credit Amount', field: 'creditAmount', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Credit Amount"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'Amount Recvd', field: 'amountRecvd', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("Amount Recvd"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'W.O. Total', field: 'woTotal', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("W.O. Total"), valueFormatter: currencyFormatter },
        { flex:1, headerName: 'W.O. Profit', field: 'woProfit', filter: 'agNumberColumnFilter', hide: !DEFAULT_COLS.has("W.O. Profit"), valueFormatter: currencyFormatter },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        minWidth: 80,
        cellStyle: { textAlign: 'right', paddingLeft: '4px', paddingRight: '4px' },
        headerClass: 'ag-right-aligned-header',
    }), []);

    return (
        <div style={{ paddingLeft: "5px", paddingRight: "5px", paddingBottom: "1rem" }}>

            {reports.length > 0 && (
                <>
                    <div className="ag-theme-alpine" style={{ height: "60vh", width: "100%", '--ag-cell-horizontal-padding': '6px', '--ag-font-size': '12px' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            onRowClicked={(e) => e.data._raw && props.openInvoice(e.data._raw)}
                            rowStyle={{ cursor: 'pointer' }}
                            rowHeight={28}
                            headerHeight={32}
                            modules={[SideBarModule, ColumnsToolPanelModule]}
                            sideBar={{ toolPanels: [{ id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel', toolPanelParams: { suppressRowGroups: true, suppressValues: true, suppressPivots: true, suppressPivotMode: true } }] }}
                            getRowStyle={(params) => {
                                if (params.node.rowPinned) return { fontWeight: '600', background: '#e9ecef', borderTop: '2px solid #adb5bd' };
                            }}
                        />
                    </div>

                    <div style={{
                        fontSize: "12px",
                        marginTop: "10px",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        display: "inline-block",
                        minWidth: "200px",
                    }}>
                        <div style={{
                            fontWeight: "600",
                            fontSize: "11px",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            color: "#6c757d",
                            marginBottom: "6px",
                            borderBottom: "1px solid #dee2e6",
                            paddingBottom: "4px",
                        }}>
                            Payment Breakdown
                        </div>
                        {methodTotals.map((item, key) => (
                            <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: "24px", padding: "2px 0" }}>
                                <span style={{ color: "#495057" }}>{item.type}</span>
                                <span style={{ fontWeight: "500", fontVariantNumeric: "tabular-nums" }}>
                                    ${item.total ? item.total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {summary.length > 0 && (
                <>
                    {summary.map((item, key) => (
                        <Row style={{marginTop:"16px"}} key={key}>
                            <Col style={{ marginLeft: "30px", borderWidth: "3px", borderRight: "ridge", marginRight: "10px" }}>
                                <Row style={{ fontWeight: "700" }}>{item.year}</Row>
                                <Row>{item.sum ? item.sum.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}</Row>
                                <Row>Total:</Row>
                                <Row>Car Count:</Row>
                            </Col>
                            {item.months.map((m, k) => (
                                <Col key={k}>
                                    <Row style={{ fontWeight: "700" }}>{m.month}</Row>
                                    <Row>{m.total ? m.total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0.00"}</Row>
                                    <Row>{m.count}</Row>
                                </Col>
                            ))}
                            {[...Array(12 - item.months.length)].map((_, i) => (
                                <Col key={`filler-${i}`}>
                                    <Row style={{ fontWeight: "700" }}></Row>
                                    <Row></Row>
                                </Col>
                            ))}
                        </Row>
                    ))}

                    <div style={{ display: "flex" }}>
                        <div style={{ height: "40rem", width: "40rem", marginLeft: "10px", zoom: "111%" }}>
                            <Line data={{ labels: CHART_LABELS, datasets: props.revenueChartDataset }} options={chartOptions} />
                        </div>
                        <div style={{ height: "40rem", width: "40rem", marginLeft: "10px", zoom: "111%" }}>
                            <Line data={{ labels: CHART_LABELS, datasets: props.carChartDataset }} options={chartOptions} />
                        </div>
                    </div>
                </>
            )}

            <br />
        </div>
    );
}
